/**
 * Due services when the odometer is entered (owner's idea "Esedékes szervizek a km megadásakor",
 * built 2026-11-02): the maintenance plan of the vehicle's type data sheet (intervals in km
 * and/or months) is compared with the vehicle's own service history (its closed work orders).
 * Only a warning, never a block. Nothing is invented: an item without an interval, or without a
 * known last time, is never reported as due.
 */
import { addMonths, daysBetween, parseServiceInterval, type ServiceInterval } from "./reminders";
import { isOilChangeLine } from "./vehicleFacts";

/** Within this many km or days before the due point an item counts as due. */
export const DUE_SOON_KM = 1000;
export const DUE_SOON_DAYS = 30;

/** Fluid fields of the data sheet that may carry a change interval ("7,0 l AW-1 · csere 60 000 km"). */
const FLUID_FIELDS = ["gearboxManual", "gearboxAuto", "gearboxOil", "transferDiff", "brakeFluid", "coolant", "powerSteering"] as const;

/**
 * Words of a work order line that mean the fluid was changed, in the program's languages
 * (lower case; a line matches when its text contains one of them).
 */
const FLUID_WORDS: Record<(typeof FLUID_FIELDS)[number], readonly string[]> = {
  gearboxManual: ["váltóolaj", "váltó olaj", "gearbox oil", "transmission oil", "getriebeöl", "převodový olej", "prevodový olej", "olej przekładniow", "ulei cutie", "ulje mjenjač", "ulje menjač", "olje menjalnik", "трансмісійн"],
  gearboxAuto: ["automata váltó", "atf", "automatic transmission", "automatikgetriebe", "dsg"],
  gearboxOil: ["váltóolaj", "váltó olaj", "gearbox oil", "transmission oil", "getriebeöl", "atf"],
  transferDiff: ["osztómű", "differenciál", "differential", "transfer case", "verteilergetriebe", "diferenciál", "dyferencjał", "diferențial", "diferencijal", "раздатк"],
  brakeFluid: ["fékfolyadék", "brake fluid", "bremsflüssigkeit", "brzdová kapalina", "brzdová kvapalina", "płyn hamulcow", "lichid de frână", "lichid frana", "kočiona tekućina", "kočiona tečnost", "zavorna tekočina", "гальмівн"],
  coolant: ["hűtőfolyadék", "fagyálló", "coolant", "antifreeze", "kühlmittel", "frostschutz", "chladicí kapalina", "chladiaca kvapalina", "płyn chłodnicz", "antigel", "rashladna tekućina", "rashladna tečnost", "hladilna tekočina", "антифриз", "охолоджуюч"],
  powerSteering: ["szervoolaj", "szervó", "szervo", "power steering", "servoöl", "servo", "wspomagan", "гідропідсилювач"],
};

/** Generic words of a plan item that say nothing about the part ("csere", "replace" ...). */
const GENERIC = new Set(["csere", "cseréje", "cserélni", "ellenőrzés", "replace", "replacement", "change", "check", "wechsel", "wechseln", "austausch", "prüfen", "výměna", "výmena", "wymiana", "înlocuire", "zamjena", "zamena", "menjava", "заміна", "és", "and", "und", "a", "az", "the", "db", "pcs", "stk", "ks", "szt"]);

/** One item of the maintenance plan read from the data sheet. */
export interface PlanItem {
  /** "oilChange", a fluid field, or "m:<name>" for a line of the maintenance plan. */
  key: string;
  /** The data sheet field it came from. */
  field: string;
  /** Name of a maintenance plan line as written on the sheet (in the workshop's language); null for oil and fluids (the program's own text). */
  label: string | null;
  interval: ServiceInterval;
}

const lower = (s: string) => s.toLowerCase().replace(/\s+/g, " ").trim();
const words = (s: string) => lower(s).split(/[^\p{L}\p{N}]+/u).filter(Boolean);

/**
 * The maintenance plan of a data sheet: the oil change interval, the fluids that give a change
 * interval, and the "what: when" lines of the maintenance plan. The first readable value of a
 * field wins (the sheet lists the best voted first). Lines without a readable interval are left out.
 */
export function maintenancePlan(facts: readonly { field: string; value: string }[]): PlanItem[] {
  const out: PlanItem[] = [];
  const seen = new Set<string>();
  const add = (item: PlanItem) => {
    if (seen.has(item.key)) return;
    seen.add(item.key);
    out.push(item);
  };
  for (const f of facts) {
    if (f.field === "serviceInterval") {
      const interval = parseServiceInterval(f.value);
      if (interval) add({ key: "oilChange", field: f.field, label: null, interval });
    } else if ((FLUID_FIELDS as readonly string[]).includes(f.field)) {
      // "nincs előírva" / "lifetime" lines have no number and are left out by the parser
      const interval = parseServiceInterval(f.value);
      if (interval) add({ key: f.field, field: f.field, label: null, interval });
    } else if (f.field === "maintenance") {
      const m = f.value.match(/^([^:]{2,80}):\s*(.+)$/s);
      if (!m) continue;
      const interval = parseServiceInterval(m[2]);
      const label = m[1]!.trim();
      if (interval && significantWords(label).length > 0) add({ key: `m:${lower(label)}`, field: f.field, label, interval });
    }
  }
  return out;
}

function significantWords(label: string): string[] {
  return words(label).filter((w) => w.length >= 3 && !GENERIC.has(w) && !/^\d+$/.test(w));
}

/** The start of a word that is compared (so "gyújtógyertyák" finds "gyújtógyertya csere"). */
const stem = (w: string) => w.slice(0, Math.max(4, w.length - 3));

/** Whether a work order line is the given plan item being done. */
export function lineMatchesPlanItem(item: PlanItem, line: { kind: string; description: string }): boolean {
  if (line.kind !== "part" && line.kind !== "labour") return false;
  if (item.key === "oilChange") return isOilChangeLine(line);
  const text = lower(line.description);
  if (item.field !== "maintenance") return (FLUID_WORDS[item.field as keyof typeof FLUID_WORDS] ?? []).some((w) => text.includes(w));
  const lineWords = words(line.description);
  return significantWords(item.label ?? "").every((w) => lineWords.some((lw) => lw.startsWith(stem(w))));
}

export interface HistoryOrder<L> {
  number: string;
  status: string;
  closedAt: string | null;
  odometerKm: number | null;
  items: readonly L[];
}

type Line = { kind: string; description: string; approval?: string };

export interface DueService<L extends Line = Line> {
  item: PlanItem;
  state: "due" | "overdue";
  /** The last time it was done: the closed work order, its odometer reading (null: unknown) and day. */
  last: { orderNumber: string; km: number | null; date: string };
  /** Where it falls due by km and/or by date (null: that part of the interval is not known or not computable). */
  dueKm: number | null;
  dueDate: string | null;
  /** How far past the due point (positive) or before it (negative); null when not computable. */
  kmOver: number | null;
  daysOver: number | null;
  /** The lines of that work order that did it (offered to add again). */
  lines: L[];
}

export interface DueServicesResult<L extends Line = Line> {
  /** The type data sheet has a maintenance plan at all. */
  hasPlan: boolean;
  /** Due or overdue items, overdue first. */
  due: DueService<L>[];
  /** Plan items never seen in the history (shown only as a short hint, never as due). */
  noHistory: PlanItem[];
}

/**
 * Which items of the maintenance plan are due or overdue at the given odometer reading and day.
 * An item with both km and months is due at whichever comes first. Declined lines and work orders
 * that are not closed do not count as done. A km interval needs both readings (the last one and
 * the current one, the current not lower); a time interval needs only the day.
 */
export function dueServices<L extends Line>(input: {
  facts: readonly { field: string; value: string }[];
  orders: readonly HistoryOrder<L>[];
  currentKm: number | null | undefined;
  /** Today, YYYY-MM-DD. */
  today: string;
}): DueServicesResult<L> {
  const plan = maintenancePlan(input.facts);
  const closed = input.orders
    .filter((o) => o.status === "closed" && o.closedAt)
    .sort((a, b) => b.closedAt!.localeCompare(a.closedAt!));
  const km = typeof input.currentKm === "number" && Number.isFinite(input.currentKm) && input.currentKm >= 0 ? input.currentKm : null;
  const due: DueService<L>[] = [];
  const noHistory: PlanItem[] = [];
  for (const item of plan) {
    let lastOrder: HistoryOrder<L> | undefined;
    let lines: L[] = [];
    for (const o of closed) {
      lines = o.items.filter((l) => l.approval !== "declined" && lineMatchesPlanItem(item, l));
      if (lines.length > 0) {
        lastOrder = o;
        break;
      }
    }
    if (!lastOrder) {
      noHistory.push(item);
      continue;
    }
    const lastDate = lastOrder.closedAt!.slice(0, 10);
    const lastKm = lastOrder.odometerKm;
    const kmKnown = item.interval.km !== null && lastKm !== null && km !== null && km >= lastKm;
    const dueKm = item.interval.km !== null && lastKm !== null ? lastKm + item.interval.km : null;
    const dueDate = item.interval.months !== null ? addMonths(lastDate, item.interval.months) : null;
    const kmOver = kmKnown && dueKm !== null ? km! - dueKm : null;
    const daysOver = dueDate !== null ? daysBetween(dueDate, input.today) : null;
    const overdue = (kmOver !== null && kmOver > 0) || (daysOver !== null && daysOver > 0);
    const soon = (kmOver !== null && kmOver > -DUE_SOON_KM) || (daysOver !== null && daysOver > -DUE_SOON_DAYS);
    if (!overdue && !soon) continue;
    due.push({ item, state: overdue ? "overdue" : "due", last: { orderNumber: lastOrder.number, km: lastKm, date: lastDate }, dueKm, dueDate, kmOver, daysOver, lines });
  }
  due.sort((a, b) => Number(b.state === "overdue") - Number(a.state === "overdue"));
  return { hasPlan: plan.length > 0, due, noHistory };
}
